import { OrgAPIKeyService, OrgAPIKey } from '../../../../services/org-api-keys.ts';
import { ToastService } from '../../../../services/toast.ts';
import '../../../../components/library/ui/app-button.ts';
import '../../../../components/library/ui/app-badge.ts';
import '../../../../components/library/ui/app-icon.ts';
import '../../../../components/library/ui/app-modal.ts';

export class OrgApiKeysTab extends HTMLElement {
  private _orgId: string = '';
  private _keys: OrgAPIKey[] = [];
  private _toastService = ToastService.getInstance();
  private _apiKeyService = OrgAPIKeyService.getInstance();
  
  private _showCreateModal: boolean = false;
  private _isSaving: boolean = false;
  private _newSecretKey: string | null = null;
  private _isLoading: boolean = false;
  private _initialized: boolean = false;

  constructor() {
    super();
  }

  static get observedAttributes() {
    return ['org-id'];
  }

  set orgId(val: string) {
    if (this._orgId !== val) {
      this._orgId = val;
      if (this.isConnected && this._orgId) {
        this.loadKeys();
      }
    }
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (oldVal === newVal) return;
    if (name === 'org-id') {
      this.orgId = newVal;
    }
  }

  connectedCallback() {
    if (!this._initialized) {
      this.addEventListener('change', (e) => e.stopPropagation());
      this.addEventListener('input', (e) => e.stopPropagation());
      this._initialized = true;
    }
    if (this._orgId && this._keys.length === 0 && !this._isLoading) {
      this.loadKeys();
    } else {
      this.render();
    }
  }

  private async loadKeys() {
    if (!this._orgId) return;
    this._isLoading = true;
    this.render();
    try {
      this._keys = (await this._apiKeyService.list(this._orgId)) || [];
    } catch (err: any) {
      this._toastService.error(err.message || 'Failed to load API keys');
    } finally {
      this._isLoading = false;
      this.render();
    }
  }

  private _renderTimeout: any = null;
  render() {
    if (this._renderTimeout) return;
    this._renderTimeout = setTimeout(() => {
      this._renderInternal();
      this._renderTimeout = null;
    }, 0);
  }

  private _renderInternal() {
    this.innerHTML = `
      <style>
        .keys-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 8px;
          font-family: var(--font-mono);
        }
        .keys-table th {
          text-align: left;
          padding: 12px;
          color: var(--text-dim);
          font-size: 0.75rem;
          text-transform: uppercase;
        }
        .keys-table td {
          padding: 14px 12px;
          background: rgba(255,255,255,0.03);
          border-top: 1px solid var(--border-sketch);
          border-bottom: 1px solid var(--border-sketch);
        }
        .keys-table td:first-child {
          border-left: 1px solid var(--border-sketch);
          border-radius: 4px 0 0 4px;
        }
        .keys-table td:last-child {
          border-right: 1px solid var(--border-sketch);
          border-radius: 0 4px 4px 0;
        }
        .key-type-select {
          appearance: none;
          background: rgba(255,255,255,0.03);
          border: 1px solid var(--border-sketch);
          color: var(--text-main);
          padding: 10px 32px 10px 12px;
          border-radius: 4px;
          font-family: var(--font-mono);
          font-size: 0.85rem;
          cursor: pointer;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23f59e0b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 8px center;
          background-size: 14px;
          transition: all 0.2s ease;
        }
        .key-type-select:hover {
          border-color: var(--accent);
          background-color: rgba(255,255,255,0.06);
        }
        .key-type-select:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.1);
        }
        .key-type-select option {
          background: #1a1a1a;
          color: white;
        }
      </style>

      <div style="display: flex; flex-direction: column; gap: 24px; max-width: 900px;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div>
            <h3 style="margin: 0 0 4px 0; color: var(--text-main); font-family: var(--font-sketch); font-size: 1.25rem;">API Keys</h3>
            <p style="margin: 0; font-size: 0.85rem; color: var(--text-dim); font-family: var(--font-mono);">
              Publishable keys identify your org in frontend clients. Secret keys authorize backend server calls.
            </p>
          </div>
          <app-button id="btn-show-create" icon="plus" size="sm" variant="white" square data-tooltip="Create API Key"></app-button>
        </div>

        <div class="sketchy-card" style="padding: 16px;">
          ${this._isLoading ? `
            <div style="text-align: center; padding: 48px; color: var(--text-dim); font-family: var(--font-mono); font-size: 0.9rem;">
              LOADING_API_KEYS...
            </div>
          ` : this._keys.length === 0 ? `
            <div style="text-align: center; padding: 48px; border: 1px dashed var(--border-sketch); border-radius: 8px; color: var(--text-dim); font-family: var(--font-mono); font-size: 0.9rem;">
              No API keys found. Click the + button above to create one.
            </div>
          ` : `
            <table class="keys-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Key / Prefix</th>
                  <th>Last Used</th>
                  <th>Created</th>
                  <th style="width: 50px;"></th>
                </tr>
              </thead>
              <tbody>
                ${this._keys.map(key => `
                  <tr>
                    <td>
                      <span style="color: var(--text-main); font-weight: bold; font-family: var(--font-mono); font-size: 0.9rem;">${key.name}</span>
                    </td>
                    <td>
                      <app-badge variant="${key.type === 'publishable' ? 'blue' : 'amber'}">${key.type}</app-badge>
                    </td>
                    <td>
                      <span style="font-family: var(--font-mono); font-size: 0.8rem; background: rgba(255,255,255,0.05); padding: 4px 8px; border-radius: 4px; border: 1px solid var(--border-sketch);">
                        ${key.key ? (key.type === 'secret' ? `${key.key}••••••••` : key.key) : '—'}
                      </span>
                    </td>
                    <td style="color: var(--text-dim); font-size: 0.85rem;">
                      ${key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td style="color: var(--text-dim); font-size: 0.85rem;">
                      ${new Date(key.created_at).toLocaleDateString()}
                    </td>
                    <td style="text-align: right;">
                      <app-button variant="ghost" size="sm" class="btn-revoke-key" data-id="${key.id}" icon="trash" data-tooltip="Revoke Key"></app-button>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          `}
        </div>
      </div>

      <div class="modal-container"></div>
    `;

    const modalContainer = this.querySelector('.modal-container');
    if (modalContainer) {
      if (this._showCreateModal) {
        modalContainer.innerHTML = `
          <app-modal id="create-key-modal" open size="sm">
            <div slot="header">Create API Key</div>
            <div style="padding: 20px;">
              <div style="margin-bottom: 20px;">
                <label style="display: block; margin-bottom: 8px; font-size: 0.75rem; color: var(--text-dim); font-family: var(--font-mono);">KEY NAME</label>
                <input type="text" id="input-key-name" placeholder="e.g. Backend Production Service" 
                       style="width: 100%; padding: 10px 12px; background: rgba(255,255,255,0.03); border: 1px solid var(--border-sketch); color: white; border-radius: 4px; font-family: var(--font-mono); font-size: 0.9rem;">
              </div>
              <div>
                <label style="display: block; margin-bottom: 8px; font-size: 0.75rem; color: var(--text-dim); font-family: var(--font-mono);">KEY TYPE</label>
                <select id="select-key-type" class="key-type-select" style="width: 100%;">
                  <option value="secret">Secret Key (Server-to-server operations)</option>
                  <option value="publishable">Publishable Key (Client frontend flows)</option>
                </select>
              </div>
            </div>
            <div slot="actions">
              <app-button variant="ghost" size="sm" id="btn-cancel-create" icon="x" data-tooltip="Cancel"></app-button>
              <app-button variant="primary" size="sm" id="btn-submit-create" icon="check" ${this._isSaving ? 'loading' : ''} data-tooltip="Create Key"></app-button>
            </div>
          </app-modal>
        `;
      } else if (this._newSecretKey) {
        modalContainer.innerHTML = `
          <app-modal id="reveal-secret-modal" open size="md">
            <div slot="header">Save Your Secret Key</div>
            <div style="padding: 20px;">
              <div style="display: flex; align-items: center; gap: 8px; color: #f59e0b; margin-bottom: 12px;">
                <app-icon name="warning" size="18"></app-icon>
                <span style="font-weight: bold; font-family: var(--font-mono); font-size: 0.85rem;">This key will never be shown again!</span>
              </div>
              <p style="margin: 0 0 16px 0; font-size: 0.85rem; color: var(--text-dim); font-family: var(--font-mono);">
                Copy this secret key and store it securely on your backend server.
              </p>
              <div style="display: flex; gap: 8px;">
                <input type="text" readonly id="input-revealed-secret" value="${this._newSecretKey}" 
                       style="flex: 1; padding: 12px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-sketch); color: var(--text-main); border-radius: 4px; font-family: var(--font-mono); font-size: 0.85rem;">
                <app-button id="btn-copy-secret" variant="secondary" icon="copy" size="sm" data-tooltip="Copy Key"></app-button>
              </div>
            </div>
            <div slot="actions">
              <app-button variant="primary" size="sm" id="btn-close-secret">Done</app-button>
            </div>
          </app-modal>
        `;
      } else {
        modalContainer.innerHTML = '';
      }
    }

    this.setupListeners();
  }

  private setupListeners() {
    const showCreateBtn = this.querySelector('#btn-show-create');
    if (showCreateBtn) {
      showCreateBtn.addEventListener('click', () => {
        this._showCreateModal = true;
        this.render();
      });
    }

    const cancelCreateBtn = this.querySelector('#btn-cancel-create');
    if (cancelCreateBtn) {
      cancelCreateBtn.addEventListener('click', () => {
        this._showCreateModal = false;
        this.render();
      });
    }

    const submitCreateBtn = this.querySelector('#btn-submit-create');
    if (submitCreateBtn) {
      submitCreateBtn.addEventListener('click', async () => {
        const nameInput = this.querySelector('#input-key-name') as HTMLInputElement;
        const typeSelect = this.querySelector('#select-key-type') as HTMLSelectElement;
        
        const name = nameInput?.value?.trim();
        const type = typeSelect?.value as 'publishable' | 'secret';

        if (!name) {
          this._toastService.error('Name is required');
          return;
        }

        this._isSaving = true;
        this.render();

        try {
          const res = await this._apiKeyService.create(this._orgId, name, type);
          this._toastService.success('API key created');
          this._showCreateModal = false;
          
          if (type === 'secret' && res.key) {
            this._newSecretKey = res.key;
          }
          
          await this.loadKeys();
        } catch (err: any) {
          this._toastService.error(err.message || 'Failed to create API key');
        } finally {
          this._isSaving = false;
          this.render();
        }
      });
    }

    const copySecretBtn = this.querySelector('#btn-copy-secret');
    if (copySecretBtn && this._newSecretKey) {
      copySecretBtn.addEventListener('click', () => {
        navigator.clipboard.writeText(this._newSecretKey!);
        this._toastService.success('Secret key copied to clipboard');
      });
    }

    const closeSecretBtn = this.querySelector('#btn-close-secret');
    if (closeSecretBtn) {
      closeSecretBtn.addEventListener('click', () => {
        this._newSecretKey = null;
        this.render();
      });
    }

    const revokeBtns = this.querySelectorAll('.btn-revoke-key');
    revokeBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).dataset.id;
        if (!id) return;
        
        if (!confirm('Are you sure you want to revoke this API key? Any applications using it will immediately lose access.')) return;
        
        try {
          await this._apiKeyService.revoke(this._orgId, id);
          this._toastService.success('API key revoked');
          await this.loadKeys();
        } catch (err: any) {
          this._toastService.error(err.message || 'Failed to revoke API key');
        }
      });
    });
  }
}

if (!customElements.get('org-api-keys-tab')) {
  customElements.define('org-api-keys-tab', OrgApiKeysTab);
}
